import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Component, lazy, Suspense, useLayoutEffect } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { ThemeProvider, useTheme } from 'next-themes';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import LandingPage from './pages/LandingPage';
import NotFoundPage from './pages/NotFoundPage';
import { LazyChunksSuspenseFallback } from '@/components/RouteLoadingShell';
import { AuthProvider, useAuth } from './contexts/AuthContext';
const AuthPage = lazy(() => import('./pages/AuthPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const RoomPage = lazy(() => import('./pages/RoomPage'));
const ViewerPage = lazy(() => import('./pages/ViewerPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PublicChangelogPage = lazy(() => import('./pages/PublicChangelogPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const ConferencesUseCasePage = lazy(() => import('./pages/ConferencesUseCasePage'));
const PresentationsUseCasePage = lazy(() => import('./pages/PresentationsUseCasePage'));
const TimerEventProductionUseCasePage = lazy(
  () => import('./pages/TimerEventProductionUseCasePage'),
);
const MeetingTimerUseCasePage = lazy(() => import('./pages/MeetingTimerUseCasePage'));
const OnlineTimerEducationUseCasePage = lazy(
  () => import('./pages/OnlineTimerEducationUseCasePage'),
);
const StreamTimerUseCasePage = lazy(() => import('./pages/StreamTimerUseCasePage'));
const BroadcastTimerUseCasePage = lazy(() => import('./pages/BroadcastTimerUseCasePage'));
const WorkoutTimerUseCasePage = lazy(() => import('./pages/WorkoutTimerUseCasePage'));
const TradeShowTimerUseCasePage = lazy(() => import('./pages/TradeShowTimerUseCasePage'));
const WebinarTimerUseCasePage = lazy(() => import('./pages/WebinarTimerUseCasePage'));
const ChurchTimerUseCasePage = lazy(() => import('./pages/ChurchTimerUseCasePage'));
const MarketingArticlePage = lazy(() => import('./pages/MarketingArticlePage'));
const GuidesIndexPage = lazy(() => import('./pages/GuidesIndexPage'));
import './App.css';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Ошибка в приложении:', error, errorInfo);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5'
        }}>
          <h1 style={{ color: '#dc2626', marginBottom: '1rem' }}>Произошла ошибка</h1>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            {this.state.error?.message || 'Неизвестная ошибка'}
          </p>
          <Button
            variant="cta"
            size="cta"
            onClick={() => window.location.reload()}
          >
            Перезагрузить страницу
          </Button>
          <details style={{ marginTop: '2rem', textAlign: 'left', maxWidth: '600px' }}>
            <summary style={{ cursor: 'pointer', color: '#666' }}>Детали ошибки</summary>
            <pre style={{ 
              background: '#fff', 
              padding: '1rem', 
              borderRadius: '4px',
              overflow: 'auto',
              marginTop: '0.5rem',
              fontSize: '0.875rem'
            }}>
              {this.state.error?.stack}
            </pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Маршруты, где `color-scheme` на `<html>` следует теме приложения (остальное — светлый маркетинговый фон).
 * Неизвестные пути (SPA 404) сюда не входят — для них принудительно `light`, без второго эффекта на странице 404.
 */
function isThemeDrivenAppPath(pathname: string): boolean {
  if (pathname === '/auth' || pathname.startsWith('/auth/')) return true;
  if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) return true;
  if (pathname === '/account' || pathname.startsWith('/account/')) return true;
  if (pathname === '/docs' || pathname.startsWith('/docs/')) return true;
  if (pathname.startsWith('/room/')) return true;
  if (pathname.startsWith('/viewer/')) return true;
  return false;
}

/**
 * Лендинг всегда светлый (в т.ч. нативный скроллбар). next-themes с enableColorScheme
 * перезаписывает document.documentElement.style.colorScheme в useEffect — поэтому
 * отключаем встроенную установку и задаём сами.
 */
function HtmlColorSchemeSync() {
  const { pathname } = useLocation();
  const { resolvedTheme } = useTheme();
  useLayoutEffect(() => {
    const isMarketingLight =
      pathname === '/' ||
      pathname === '/changelog' ||
      pathname === '/features' ||
      pathname === '/pricing' ||
      pathname === '/faq' ||
      pathname === '/guides' ||
      pathname.startsWith('/use-cases/') ||
      pathname.startsWith('/guides/') ||
      pathname.startsWith('/compare/');

    if (isMarketingLight || !isThemeDrivenAppPath(pathname)) {
      document.documentElement.style.colorScheme = 'light';
      return;
    }
    const isDark =
      resolvedTheme === 'dark' ||
      (resolvedTheme === undefined && document.documentElement.classList.contains('dark'));
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  }, [pathname, resolvedTheme]);
  return null;
}

function RouteScrollReset() {
  const { pathname, hash } = useLocation();
  useLayoutEffect(() => {
    if (hash) {
      const id = hash.replace(/^#/, '');
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);
  return null;
}

function App() {
  console.log('📱 Компонент App рендерится');
  return (
    <ErrorBoundary>
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        storageKey="theme"
        enableSystem
        enableColorScheme={false}
      >
        <TooltipProvider>
          <BrowserRouter>
            <HtmlColorSchemeSync />
            <RouteScrollReset />
            <AuthProvider>
              <Suspense fallback={<LazyChunksSuspenseFallback />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/changelog" element={<PublicChangelogPage />} />
                  <Route path="/features" element={<FeaturesPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/faq" element={<FaqPage />} />
                  <Route path="/guides" element={<GuidesIndexPage />} />
                  <Route path="/use-cases/conferences" element={<ConferencesUseCasePage />} />
                  <Route path="/use-cases/presentations" element={<PresentationsUseCasePage />} />
                  <Route
                    path="/use-cases/timer-event-production"
                    element={<TimerEventProductionUseCasePage />}
                  />
                  <Route path="/use-cases/meeting-timer" element={<MeetingTimerUseCasePage />} />
                  <Route
                    path="/use-cases/online-timer-for-education"
                    element={<OnlineTimerEducationUseCasePage />}
                  />
                  <Route path="/use-cases/stream-timer" element={<StreamTimerUseCasePage />} />
                  <Route
                    path="/use-cases/broadcast-timer"
                    element={<BroadcastTimerUseCasePage />}
                  />
                  <Route path="/use-cases/workout-timer" element={<WorkoutTimerUseCasePage />} />
                  <Route
                    path="/use-cases/trade-show-timer"
                    element={<TradeShowTimerUseCasePage />}
                  />
                  <Route path="/use-cases/webinar-timer" element={<WebinarTimerUseCasePage />} />
                  <Route path="/use-cases/church-timer" element={<ChurchTimerUseCasePage />} />
                  <Route
                    path="/use-cases/conferences-and-presentations"
                    element={<Navigate to="/use-cases/conferences" replace />}
                  />
                  <Route
                    path="/guides/timer-dlya-vebinara"
                    element={<Navigate to="/use-cases/webinar-timer" replace />}
                  />
                  <Route path="/guides/:slug" element={<MarketingArticlePage />} />
                  <Route path="/compare/:slug" element={<MarketingArticlePage />} />
                  <Route
                    path="/dashboard"
                    element={
                      <RequireAuth>
                        <HomePage />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <RequireAuth>
                        <AccountPage />
                      </RequireAuth>
                    }
                  />
                  <Route path="/room/:roomId" element={<RoomPage />} />
                  <Route path="/docs" element={<DashboardPage />} />
                  <Route path="/viewer/:roomId" element={<ViewerPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </AuthProvider>
          </BrowserRouter>
          <Toaster />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="min-h-screen w-full bg-[var(--bg-page)]"
        aria-busy="true"
        aria-live="polite"
      >
        <span className="sr-only">Загрузка аккаунта</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

export default App;
